import { Controller } from "@hotwired/stimulus"
import I18n from "../../i18n"

const t = (key) => I18n.t(`erp.sales.js.item_pricing.${key}`)

// Textile pricing for one estimate line: supplier price × coefficient, and the Imbretex
// refresh. Marking prices live in erp--marking-form.
// Connects to data-controller="erp--estimate-item-pricing"
export default class extends Controller {
  static targets = [
    "supplierPrice", "supplierPriceFeedback", "marginRate", "textilePrice",
    "unitPrice", "unitPriceOverride", "manualPriceReason",
    "discountType", "discountPercentageField", "discountAmountField",
    "refreshSupplierPrice"
  ]

  static values = { refreshSupplierPriceUrl: String }

  // Imbretex stores 1 cent when it has not returned a price yet.
  static UNKNOWN_SUPPLIER_PRICE = 0.01

  connect() {
    this._refreshAbortController = null
    this.#renderOverrideState()
    this.discountTypeChanged()
  }

  disconnect() {
    this._refreshAbortController?.abort()
  }

  calculateTextilePrice() {
    if (!this.hasSupplierPriceTarget || !this.hasMarginRateTarget) return

    const supplierEuros = parseFloat(this.supplierPriceTarget.value) || 0
    if (supplierEuros <= this.constructor.UNKNOWN_SUPPLIER_PRICE) {
      this.#setSupplierFeedback(t("supplier_price_unknown"), "warning")
      const overridden = this.#unitPriceFixed()
      if (this.hasTextilePriceTarget) this.textilePriceTarget.value = ""
      if (!overridden && this.hasUnitPriceTarget) this.unitPriceTarget.value = ""
      this.#setPricingUnavailable(!overridden)
      return
    }

    const enteredRate = this.marginRateTarget.value.trim()
    const marginRate = Number(enteredRate || this.marginRateTarget.dataset.inheritedMarginRate)
    if (!Number.isFinite(marginRate) || marginRate < 1) {
      this.#setSupplierFeedback(t("margin_rate_invalid"), "error")
      const overridden = this.#unitPriceFixed()
      if (this.hasTextilePriceTarget) this.textilePriceTarget.value = ""
      if (!overridden && this.hasUnitPriceTarget) this.unitPriceTarget.value = ""
      this.#setPricingUnavailable(!overridden)
      return
    }

    this.#setSupplierFeedback("", "info")
    this.#setPricingUnavailable(false)
    if (this.hasTextilePriceTarget) {
      this.textilePriceTarget.value = (supplierEuros * marginRate).toFixed(2)
    }

    if (this.#unitPriceFixed()) this.#notifySummaryChanged()
    else this.updateUnitPrice()
  }

  updateUnitPrice() {
    if (this.#unitPriceFixed()) return
    if (!this.hasUnitPriceTarget || !this.hasTextilePriceTarget) return

    this.unitPriceTarget.value = (parseFloat(this.textilePriceTarget.value) || 0).toFixed(2)
    this.unitPriceTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }

  overrideChanged() {
    const overridden = this.#renderOverrideState()
    if (overridden) {
      this.#setPricingUnavailable(false)
      this.#notifySummaryChanged()
    } else {
      this.calculateTextilePrice()
    }
  }

  discountTypeChanged() {
    const fixed = this.hasDiscountTypeTarget && this.discountTypeTarget.value === "fixed"
    if (this.hasDiscountPercentageFieldTarget) this.discountPercentageFieldTarget.classList.toggle("hidden", fixed)
    if (this.hasDiscountAmountFieldTarget) this.discountAmountFieldTarget.classList.toggle("hidden", !fixed)
  }

  // Cost × coefficient never touches a forced price, nor a price agreed with the customer (Tarifs tab): the
  // autosave brings that one back from the server
  #unitPriceFixed() {
    const overridden = this.hasUnitPriceOverrideTarget && this.unitPriceOverrideTarget.checked
    return overridden || this.element.closest("[data-erp--estimate-editor-target='item']")?.dataset.customerPrice === "true"
  }

  #setPricingUnavailable(unavailable) {
    const item = this.element.closest("[data-erp--estimate-editor-target='item']")
    if (item) item.dataset.pricingUnavailable = String(unavailable)
    if (this.hasUnitPriceTarget) this.unitPriceTarget.required = !unavailable
    this.#notifySummaryChanged()
  }

  #notifySummaryChanged() {
    this.unitPriceTarget?.dispatchEvent(new Event("input", { bubbles: true }))
  }

  #renderOverrideState() {
    const overridden = this.hasUnitPriceOverrideTarget && this.unitPriceOverrideTarget.checked
    const item = this.element.closest("[data-erp--estimate-editor-target='item']")
    const customerSupplied = item?.querySelector("[data-customer-supplied-field]")?.value === "1"

    if (this.hasUnitPriceTarget) {
      this.unitPriceTarget.readOnly = !overridden && !customerSupplied
    }
    if (this.hasManualPriceReasonTarget) {
      this.manualPriceReasonTarget.classList.toggle("hidden", !overridden)
    }
    return overridden
  }

  async refreshSupplierPrice() {
    if (!this.refreshSupplierPriceUrlValue) return

    const item = this.element.closest("[data-erp--estimate-editor-target='item']") || this.element
    const variantId = item.querySelector("input[name*='[product_variant_id]']")?.value
    if (!variantId) return this.#setSupplierFeedback(t("no_variant"), "error")

    this._refreshAbortController?.abort()
    const request = new AbortController()
    this._refreshAbortController = request

    if (this.hasRefreshSupplierPriceTarget) this.refreshSupplierPriceTarget.disabled = true
    this.#setSupplierFeedback(t("refreshing"), "info")

    try {
      const response = await fetch(this.refreshSupplierPriceUrlValue.replace("__ID__", variantId), {
        method: "POST",
        signal: request.signal,
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
        }
      })
      if (request !== this._refreshAbortController) return
      const data = await response.json().catch(() => ({}))
      const currentVariantId = item.querySelector("input[name*='[product_variant_id]']")?.value
      if (currentVariantId !== variantId) return

      if (response.ok && data.supplier_price_cents != null) {
        if (this.hasSupplierPriceTarget) {
          this.supplierPriceTarget.value = (data.supplier_price_cents / 100).toFixed(2)
          this.supplierPriceTarget.dispatchEvent(new Event("input", { bubbles: true }))
        }
        this.#setSupplierFeedback(t("refreshed"), "success")
      } else {
        this.#setSupplierFeedback(data.error || t("refresh_failed"), "error")
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.warn("[item-pricing] Supplier price refresh failed:", error)
        this.#setSupplierFeedback(t("network_error"), "error")
      }
    } finally {
      if (request === this._refreshAbortController && this.hasRefreshSupplierPriceTarget) {
        this.refreshSupplierPriceTarget.disabled = false
      }
    }
  }

  #setSupplierFeedback(message, level) {
    if (!this.hasSupplierPriceFeedbackTarget) return

    const colors = {
      info: "text-erp-muted",
      success: "text-erp-st-won-fg",
      warning: "text-erp-st-pending-fg",
      error: "text-erp-danger"
    }
    this.supplierPriceFeedbackTarget.textContent = message
    this.supplierPriceFeedbackTarget.className = `text-xs ${colors[level] || colors.info}`
  }
}
